import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import {
  adminTable,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const DetailContainer = styled.div`
  background: white;
  padding: 1.5em;
`;

const DetailTable = styled.table`
  ${adminTable};
  margin-bottom: 2em;
  font-size: 14px;

  thead {
    display: none;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.75em 1em;
    vertical-align: top;
  }

  tbody tr:nth-child(odd) td { background: #f9f9f9; }
  tbody tr:nth-child(even) td { background: white; }
  td:first-child { font-weight: bold; width: 30%; }
`;

const SubscriptionTable = styled.table`
  ${adminTable};
  font-size: 13px;
  margin-top: 1em;

  thead {
    display: none;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.75em 0.5em;
    vertical-align: top;
  }

  td:nth-child(1) { width: 5%; }
  td:nth-child(2) { width: 70%; }
  td:nth-child(3) { width: 25%; }
`;

const BackButton = styled.button`
  margin-top: 1em;
  padding: 0.6em 1.2em;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const ButtonLink = styled.button`
  display: inline-block;
  margin-left: 0.5em;
  padding: 0.25em 0.5em;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 3px;
  font-size: 0.85em;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const formatToMMDDYYYY = (value) => {
  if (!value) return "—";
  const datePart = String(value).split(/[T ]/)[0];
  const [year, month = "", day = ""] = (datePart || "").split("-");
  if (!year || year.length < 4) {
    return value;
  }
  const mm = month.padStart(2, "0") || "00";
  const dd = day.padStart(2, "0") || "00";
  return `${mm}-${dd}-${year}`;
};

export default function TeacherDetailsDisplay({
  teacher,
  subscription,
  implementations = [],
  colors,
  onBack,
  onChangePassword,
}) {
  const [implementationRows, setImplementationRows] = useState(
    implementations.length > 0 ? implementations : teacher ? [teacher] : []
  );
  const [implementationToUncertify, setImplementationToUncertify] = useState(null);

  // verify dialog state
  const [confirmVerifyOpen, setConfirmVerifyOpen] = useState(false);
  const [verifyResult, setVerifyResult] = useState({ show: false, title: "", message: "", status: "Success" });

  // uncertify dialog state
  const [confirmUncertifyOpen, setConfirmUncertifyOpen] = useState(false);
  const [uncertifyResult, setUncertifyResult] = useState({ show: false, title: "", message: "", status: "Success" });

  // fetched login info
  const [lastLogin, setLastLogin] = useState(teacher.last_login || "—");
  const [loginCount, setLoginCount] = useState(teacher.login_count || 0);

  // fetch latest login info whenever teacher changes
  useEffect(() => {
    if (!teacher.user_id) return;
    API.getTeacherDetail({ user_id: teacher.user_id })
      .then((resp) => {
        setLastLogin(resp.last_login || "—");
        setLoginCount(Number(resp.total) || 0);
      })
      .catch((err) => {
        console.error("Failed to load teacher detail:", err);
      });
  }, [teacher.user_id]);

  // handle Verify
  const handleVerifyClick = () => setConfirmVerifyOpen(true);
  const handleVerifyConfirm = async () => {
    setConfirmVerifyOpen(false);
    try {
      const resp = await API.verifyTeacherEmail({ user_id: teacher.user_id , is_verified: 1});
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setVerifyResult({
        show: true,
        title: status === "Failed" ? "Error" : "Email Sent",
        message: resp.message || (status === "Failed" ? "Failed to send verification email." : "Verification email sent."),
        status,
      });
    } catch (err) {
      setVerifyResult({
        show: true,
        title: "Error",
        message: err.message || "Failed to send verification email.",
        status: "Failed",
      });
    }
  };
  const finishVerify = () => setVerifyResult(r => ({ ...r, show: false, status: "Success" }));

  // handle Uncertify
  useEffect(() => {
    if (implementations.length > 0) {
      setImplementationRows(implementations);
    } else if (teacher) {
      setImplementationRows([teacher]);
    } else {
      setImplementationRows([]);
    }
  }, [implementations, teacher]);

  const handleUncertifyClick = (implementation) => {
    if (!implementation?.implementation_id) return;
    setImplementationToUncertify(implementation);
    setConfirmUncertifyOpen(true);
  };
  const handleUncertifyConfirm = async () => {
    const targetImplementation = implementationToUncertify;
    setConfirmUncertifyOpen(false);
    if (!targetImplementation?.implementation_id) {
      setImplementationToUncertify(null);
      return;
    }
    try {
      const resp = await API.uncertifySubscriptionTeacher({
        implementation_id: targetImplementation.implementation_id,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setUncertifyResult({
        show: true,
        title: status === "Failed" ? "Error" : "Uncertified",
        message: resp.message || (status === "Failed" ? "Uncertify failed." : "Certification removed."),
        status,
      });
      if (status !== "Failed") {
        setImplementationRows((rows) =>
          rows.map((row) =>
            row.implementation_id === targetImplementation.implementation_id
              ? { ...row, certified: null }
              : row
          )
        );
      }
    } catch (err) {
      setUncertifyResult({
        show: true,
        title: "Error",
        message: err.message || "Uncertify failed.",
        status: "Failed",
      });
    }
    setImplementationToUncertify(null);
  };
  const finishUncertify = () => setUncertifyResult(r => ({ ...r, show: false, status: "Success" }));

  return (
    <DetailContainer>
      {/* Verify confirmation */}
      <ConfirmationModal
        show={confirmVerifyOpen}
        title="Confirm Verify"
        message={`Send verification email to ${teacher.email}?`}
        onCancel={() => setConfirmVerifyOpen(false)}
        onConfirm={handleVerifyConfirm}
      />
      {/* Verify result */}
      <ConfirmationModal
        show={verifyResult.show}
        title={verifyResult.title}
        message={verifyResult.message}
        onCancel={finishVerify}
        onConfirm={finishVerify}
        status={verifyResult.status}
        single
      />

      {/* Uncertify confirmation */}
      <ConfirmationModal
        show={confirmUncertifyOpen}
        title="Confirm Uncertify"
        message={
          implementationToUncertify?.implementation_id
            ? `Remove certification for implementation #${implementationToUncertify.implementation_id}?`
            : "Remove certification for this educator?"
        }
        onCancel={() => {
          setConfirmUncertifyOpen(false);
          setImplementationToUncertify(null);
        }}
        onConfirm={handleUncertifyConfirm}
      />
      {/* Uncertify result */}
      <ConfirmationModal
        show={uncertifyResult.show}
        title={uncertifyResult.title}
        message={uncertifyResult.message}
        onCancel={finishUncertify}
        onConfirm={finishUncertify}
        status={uncertifyResult.status}
        single
      />

      <h1>Personal Information</h1>
      <DetailTable>
        <tbody>
          <tr><td>First Name</td><td>{teacher.first_name}</td></tr>
          <tr><td>Last Name</td><td>{teacher.last_name}</td></tr>
          <tr><td>Address</td><td>{teacher.street || "—"}</td></tr>
          <tr><td>City</td><td>{teacher.city || "—"}</td></tr>
          <tr><td>State</td><td>{teacher.state || "—"}</td></tr>
          <tr><td>Zip</td><td>{teacher.zip || "—"}</td></tr>
          <tr><td>Phone</td><td>{teacher.phone || "—"}</td></tr>
          <tr>
            <td>E-mail Address</td>
            <td>
              {teacher.email}&nbsp;
              <ButtonLink colors={colors} onClick={handleVerifyClick}>
                Verify
              </ButtonLink>
            </td>
          </tr>
          <tr>
            <td>Password</td>
            <td>
              ****&nbsp;
              <ButtonLink colors={colors} onClick={onChangePassword}>
                Change Password
              </ButtonLink>
            </td>
          </tr>
          <tr><td>News Letter</td><td>{teacher.news_letter ? "Yes" : "No"}</td></tr>
          <tr><td>i-Parent Board</td><td>{teacher.i_parent_board ? "Yes" : "No"}</td></tr>
          <tr><td>Last Login</td><td>{lastLogin}</td></tr>
          <tr><td>Login Count</td><td>{loginCount}</td></tr>
        </tbody>
      </DetailTable>

      <h1>Subscription Memberships</h1>
      <SubscriptionTable>
        <tbody>
          <tr className="header">
            <td colSpan={3}>
              <h1 style={{ fontSize: "12px", margin: 0 }}>
                {subscription.org_name} ({subscription.invoice_number})
              </h1>
            </td>
          </tr>
          {implementationRows.length === 0 ? (
            <tr>
              <td />
              <td colSpan={2}>No subscription implementations found for this educator.</td>
            </tr>
          ) : (
            implementationRows.map((implementation, index) => {
              const gradesList = Array.isArray(implementation.grade_records)
                ? implementation.grade_records
                    .map((g) => g?.grade_code)
                    .filter(Boolean)
                    .join(", ")
                : "";
              const isCertified = Boolean(implementation.certified);
              const studentsCount =
                implementation.students_count ??
                Number(implementation.num_of_students ?? 0);

              return (
                <tr className="entry" key={implementation.implementation_id || index}>
                  <td>{index + 1}</td>
                  <td>
                    <b>
                      Implementation #{implementation.implementation_id}
                      {implementation.school_id
                        ? ` — School #${implementation.school_id}`
                        : ""}
                      {isCertified && " (ACTIVE)"}
                    </b>
                    <br />
                    {implementation.org_name || subscription.org_name}
                    <br />
                    <b>Start:</b> {formatToMMDDYYYY(implementation.start_date)} &nbsp;
                    <b>End:</b> {formatToMMDDYYYY(implementation.end_date)}
                    <br />
                    <b>Students:</b> {studentsCount}
                    <br />
                    <b>Status:</b>&nbsp;
                    {isCertified
                      ? `Certified${
                          gradesList ? ` – Grades ${gradesList}` : ""
                        }`
                      : "Not Certified"}{" "}
                    {isCertified && (
                      <ButtonLink
                        colors={colors}
                        onClick={() => handleUncertifyClick(implementation)}
                      >
                        Uncertify
                      </ButtonLink>
                    )}
                  </td>
                  <td>
                    Access to curriculum, manage assigned implementations. (Subscription Teacher)
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </SubscriptionTable>

      <BackButton colors={colors} onClick={onBack}>
        Back to list
      </BackButton>
    </DetailContainer>
  );
}
